import { datePl, type MeDto, type PackageCandidateDto, type PackageDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, Checkbox, Drawer, Group, Modal, NumberInput, SegmentedControl, Select, Stack, Table, Text, TextInput, Textarea } from '@mantine/core';
import { IconPackage, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { ask } from '../../confirm';
import { PackageStatusBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { formatDateTime, qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { useRecipients } from './common';
import { useDemandRefresh } from './demand-ui';

const newKey = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k${Date.now()}${Math.random()}`);

/** Pakiety materiałów dla ekip (docs/14 §2.8): do przygotowania, gotowe do odbioru, wydane. */
export function PackagesPage({ me }: { me: MeDto }) {
  const [params, setParams] = useSearchParams();
  const view = params.get('widok') ?? 'aktywne';
  const openId = params.get('pakiet');
  const presetTask = params.get('nowy');
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: ['packages', view],
    queryFn: () => api<PackageDto[]>(view === 'aktywne' ? '/packages?active=true' : `/packages?status=${view}`),
  });
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };
  const canPick = can(me, 'magazyn.kompletacja');
  return (
    <>
      <PageHeader
        title="Pakiety dla ekip"
        description="Magazynier kompletuje materiały do zadania i przygotowuje je do odbioru. Kompletacja nie zmienia stanu — dopiero wydanie (z osobą odbierającą)."
        actions={
          canPick && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              Nowy pakiet
            </Button>
          )
        }
      />
      <SegmentedControl
        mb="md"
        value={view}
        onChange={(v) => set('widok', v === 'aktywne' ? null : v)}
        data={[
          { value: 'aktywne', label: 'Do przygotowania i odbioru' },
          { value: 'wydany', label: 'Wydane' },
          { value: 'anulowany', label: 'Anulowane' },
        ]}
      />
      <Card padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          {!q.data?.length ? (
            <EmptyState icon={<IconPackage size={24} />} title="Brak pakietów" hint={view === 'aktywne' && canPick ? 'Utwórz pakiet z potrzeb zaplanowanego zadania („Nowy pakiet”).' : undefined} />
          ) : (
            <Table.ScrollContainer minWidth={760}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Pakiet</Table.Th>
                    <Table.Th>Odbiór</Table.Th>
                    <Table.Th>Zadanie / ekipa</Table.Th>
                    <Table.Th ta="right">Skompletowano</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {q.data.map((p) => {
                    const req = p.lines.reduce((a, l) => a + l.required, 0);
                    const done = p.lines.reduce((a, l) => a + l.picked + l.issued, 0);
                    const missing = p.lines.filter((l) => l.missing > 0).length;
                    const attention = p.lines.some((l) => l.pendingQuantity !== null || l.toUnpick > 0);
                    return (
                      <Table.Tr key={p.id} className="row-link" onClick={() => set('pakiet', p.id)}>
                        <Table.Td>
                          <Text size="sm" fw={600}>
                            {p.number}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {p.companyCode} · {p.warehouseCode} {p.caseNumber ? `· ${p.caseNumber}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>{p.plannedPickup ? datePl(p.plannedPickup) : '—'}</Table.Td>
                        <Table.Td>
                          <Text size="sm">{p.taskNumber ?? '—'}</Text>
                          <Text size="xs" c="dimmed">
                            {p.teamName ?? ''}
                            {p.taskDateFrom ? ` · montaż ${datePl(p.taskDateFrom)}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          {req ? Math.round((done / req) * 100) : 0}%
                          {missing > 0 && (
                            <Text size="xs" c="orange">
                              braki: {missing}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <PackageStatusBadge status={p.status} />
                            {attention && (
                              <Badge size="sm" color="red" variant="light">
                                Zmiana do decyzji
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </QueryState>
      </Card>
      {(creating || (!!presetTask && canPick)) && (
        <CreatePackage
          presetTaskId={presetTask}
          onClose={() => (setCreating(false), set('nowy', null))}
          onCreated={(id) => {
            setCreating(false);
            const n = new URLSearchParams(params);
            n.delete('nowy');
            n.set('pakiet', id);
            setParams(n, { replace: true });
          }}
        />
      )}
      <PackageDrawer id={openId} onClose={() => set('pakiet', null)} me={me} />
    </>
  );
}

function CreatePackage({ presetTaskId, onClose, onCreated }: { presetTaskId?: string | null; onClose: () => void; onCreated: (id: string) => void }) {
  const q = useQuery({ queryKey: ['packages', 'candidates'], queryFn: () => api<PackageCandidateDto[]>('/packages/candidates') });
  const [taskKey, setTaskKey] = useState<string | null>(null);
  useEffect(() => {
    // wejście z zapotrzebowania: zadanie wybrane od razu (pierwszy magazyn z potrzebami)
    const c = presetTaskId ? q.data?.find((x) => x.taskId === presetTaskId) : undefined;
    if (c && !taskKey) setTaskKey(`${c.taskId}|${c.warehouseId}`);
  }, [presetTaskId, q.data, taskKey]);
  const cand = q.data?.find((c) => `${c.taskId}|${c.warehouseId}` === taskKey);
  const [qty, setQty] = useState<Record<string, number>>({});
  useEffect(() => setQty(Object.fromEntries((cand?.lines ?? []).map((l) => [l.demandLineId, l.open]))), [cand]);
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () => api<PackageDto>('/packages', { method: 'POST', body: { taskId: cand!.taskId, lines: Object.entries(qty).filter(([, v]) => v > 0).map(([demandLineId, quantity]) => ({ demandLineId, quantity })) } }),
    onSuccess: (p) => {
      notifyOk(`Utworzono pakiet ${p.number}`);
      void qc.invalidateQueries({ queryKey: ['packages'] });
      onCreated(p.id);
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Nowy pakiet" size="lg">
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Stack>
          {!q.data?.length ? (
            <EmptyState compact title="Brak potrzeb do spakowania" hint="Pakiet powstaje z potrzeb zadania (z umowy lub zgłoszenia ekipy), które nie są jeszcze w pakietach." />
          ) : (
            <Select
              label="Zadanie"
              data={q.data.map((c) => ({ value: `${c.taskId}|${c.warehouseId}`, label: `${c.taskNumber} — ${c.caseNumber ?? ''} ${c.dateFrom ? `(montaż ${datePl(c.dateFrom)})` : '(bez terminu)'}${c.teamName ? ` · ${c.teamName}` : ''}` }))}
              value={taskKey}
              onChange={setTaskKey}
              searchable
            />
          )}
          {cand && (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Produkt</Table.Th>
                  <Table.Th ta="right">Do spakowania</Table.Th>
                  <Table.Th ta="right">Zarezerw.</Table.Th>
                  <Table.Th w={130}>Ilość</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {cand.lines.map((l) => (
                  <Table.Tr key={l.demandLineId}>
                    <Table.Td>{l.name}</Table.Td>
                    <Table.Td ta="right">
                      {qtyPl(l.open)} {l.unit}
                    </Table.Td>
                    <Table.Td ta="right">{qtyPl(l.reserved)}</Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" min={0} max={l.open} decimalScale={3} decimalSeparator="," value={qty[l.demandLineId] ?? 0} onChange={(v) => setQty({ ...qty, [l.demandLineId]: Number(v) || 0 })} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!cand || !Object.values(qty).some((v) => v > 0)}>
              Utwórz pakiet
            </Button>
          </Group>
        </Stack>
      </QueryState>
    </Modal>
  );
}

export function PackageDrawer({ id, onClose, me }: { id: string | null; onClose: () => void; me: MeDto }) {
  const q = useQuery({ queryKey: ['package', id], queryFn: () => api<PackageDto>(`/packages/${id}`), enabled: !!id });
  const p = q.data;
  const qc = useQueryClient();
  const refreshAll = useDemandRefresh();
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [pickup, setPickup] = useState({ plannedPickup: '', pickupPlace: '', note: '' });
  const [issuing, setIssuing] = useState(false);
  useEffect(() => {
    if (!p) return;
    setPicked(Object.fromEntries(p.lines.map((l) => [l.id, l.picked])));
    setNotes(Object.fromEntries(p.lines.map((l) => [l.id, l.substituteNote])));
    setPickup({ plannedPickup: p.plannedPickup ?? '', pickupPlace: p.pickupPlace, note: p.note });
  }, [p]);
  const done = (msg: string) => (r: PackageDto) => {
    qc.setQueryData(['package', id], r);
    void qc.invalidateQueries({ queryKey: ['packages'] });
    refreshAll();
    notifyOk(msg);
  };
  const dirtyPick = !!p && p.lines.some((l) => (picked[l.id] ?? l.picked) !== l.picked || (notes[l.id] ?? '') !== l.substituteNote);
  const savePick = useMutation({
    mutationFn: () => api<PackageDto>(`/packages/${id}/pick`, { method: 'POST', body: { lines: p!.lines.map((l) => ({ lineId: l.id, picked: picked[l.id] ?? l.picked, substituteNote: notes[l.id] ?? '' })) } }),
    onSuccess: done('Zapisano kompletację'),
    onError: notifyError,
  });
  const ready = useMutation({ mutationFn: () => api<PackageDto>(`/packages/${id}/ready`, { method: 'POST' }), onSuccess: done('Pakiet do odbioru'), onError: notifyError });
  const saveInfo = useMutation({
    mutationFn: () => api<PackageDto>(`/packages/${id}`, { method: 'PUT', body: { plannedPickup: pickup.plannedPickup || null, pickupPlace: pickup.pickupPlace, note: pickup.note, recipientUserId: p?.recipientUserId ?? null } }),
    onSuccess: done('Zapisano odbiór'),
    onError: notifyError,
  });
  const cancel = useMutation({ mutationFn: (reason: string) => api<PackageDto>(`/packages/${id}/cancel`, { method: 'POST', body: { reason } }), onSuccess: done('Anulowano pakiet'), onError: notifyError });
  const decide = useMutation({
    mutationFn: (x: { lineId: string; accept: boolean }) => api(`/demand-lines/${x.lineId}/pending`, { method: 'POST', body: { accept: x.accept, reason: x.accept ? 'Przyjęto zmianę od ekipy' : 'Zmiana odrzucona' } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['package', id] });
      refreshAll();
      notifyOk('Zapisano decyzję');
    },
    onError: notifyError,
  });
  const open = !!p && p.status !== 'wydany' && p.status !== 'anulowany';
  const canPick = can(me, 'magazyn.kompletacja') && open;
  return (
    <Drawer opened={!!id} onClose={onClose} position="right" size="xl" title={p ? `Pakiet ${p.number}` : 'Pakiet'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {p && (
          <Stack>
            <Group gap="xs">
              <PackageStatusBadge status={p.status} />
              {p.caseNumber &&
                (can(me, 'sprawy.podglad') ? (
                  <Anchor component={Link} to={`/sprawy/${p.caseId}?zakladka=materialy`} size="sm">
                    {p.caseNumber}
                  </Anchor>
                ) : (
                  <Text size="sm">{p.caseNumber}</Text>
                ))}
              <Text size="sm" c="dimmed">
                zadanie {p.taskNumber ?? '—'}
                {p.teamName ? ` · ${p.teamName}` : ''}
                {p.taskDateFrom ? ` · montaż ${datePl(p.taskDateFrom)}` : ''}
              </Text>
            </Group>
            <SectionCard title="Odbiór" padding="sm">
              <Group align="flex-end" wrap="wrap">
                <TextInput label="Planowany odbiór" type="date" value={pickup.plannedPickup} onChange={(e) => setPickup({ ...pickup, plannedPickup: e.currentTarget.value })} disabled={!canPick} w={{ base: '100%', sm: 170 }} />
                <TextInput label="Miejsce odbioru" value={pickup.pickupPlace} onChange={(e) => setPickup({ ...pickup, pickupPlace: e.currentTarget.value })} disabled={!canPick} style={{ flex: 1 }} />
              </Group>
              <Textarea mt="xs" label="Uwagi dla ekipy" autosize minRows={1} value={pickup.note} onChange={(e) => setPickup({ ...pickup, note: e.currentTarget.value })} disabled={!canPick} />
              {canPick && (pickup.plannedPickup !== (p.plannedPickup ?? '') || pickup.pickupPlace !== p.pickupPlace || pickup.note !== p.note) && (
                <Group justify="flex-end" mt="xs">
                  <Button size="xs" onClick={() => saveInfo.mutate()} loading={saveInfo.isPending}>
                    Zapisz
                  </Button>
                </Group>
              )}
            </SectionCard>
            <SectionCard title="Pozycje" padding="sm">
              <Table.ScrollContainer minWidth={640}>
                <Table verticalSpacing={4}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Produkt</Table.Th>
                      <Table.Th ta="right">Potrzeba</Table.Th>
                      <Table.Th ta="right">Wydano</Table.Th>
                      <Table.Th w={130}>Skompletowano</Table.Th>
                      <Table.Th ta="right">Brak</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {p.lines.map((l) => (
                      <Table.Tr key={l.id}>
                        <Table.Td>
                          <Text size="sm">{l.name}</Text>
                          {canPick ? (
                            <TextInput size="xs" mt={2} placeholder="zamiennik / uwaga dla ekipy" value={notes[l.id] ?? ''} onChange={(e) => setNotes({ ...notes, [l.id]: e.currentTarget.value })} />
                          ) : l.substituteNote ? (
                            <Text size="xs" c="dimmed">
                              {l.substituteNote}
                            </Text>
                          ) : null}
                          {l.pendingQuantity !== null && (
                            <Alert color="red" variant="light" p={6} mt={4}>
                              <Text size="xs">Ekipa zmieniła potrzebę na {qtyPl(l.pendingQuantity)} {l.unit} (już skompletowano) — zdecyduj:</Text>
                              <Group gap={4} mt={4}>
                                <Button size="compact-xs" color="red" onClick={() => decide.mutate({ lineId: l.demandLineId, accept: true })} loading={decide.isPending}>
                                  Przyjmij i rozkompletuj
                                </Button>
                                <Button size="compact-xs" variant="default" onClick={() => decide.mutate({ lineId: l.demandLineId, accept: false })}>
                                  Odrzuć
                                </Button>
                              </Group>
                            </Alert>
                          )}
                          {l.toUnpick > 0 && l.pendingQuantity === null && (
                            <Text size="xs" c="red">
                              Potrzeba zmalała — rozkompletuj {qtyPl(l.toUnpick)} {l.unit}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td ta="right">
                          {qtyPl(l.required)} {l.unit}
                        </Table.Td>
                        <Table.Td ta="right">{l.issued ? qtyPl(l.issued) : '—'}</Table.Td>
                        <Table.Td>
                          {canPick ? (
                            <NumberInput size="xs" min={0} max={l.required - l.issued} decimalScale={3} decimalSeparator="," value={picked[l.id] ?? l.picked} onChange={(v) => setPicked({ ...picked, [l.id]: Number(v) || 0 })} />
                          ) : (
                            qtyPl(l.picked)
                          )}
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" c={l.missing ? 'orange' : 'dimmed'} fw={l.missing ? 700 : 400}>
                            {l.missing ? qtyPl(l.missing) : '—'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
              {canPick && (
                <Group justify="space-between" mt="xs">
                  <Button size="xs" variant="subtle" onClick={() => setPicked(Object.fromEntries(p.lines.map((l) => [l.id, Math.max(0, l.required - l.issued - l.missing)])))}>
                    Skompletuj wszystko dostępne
                  </Button>
                  <Button size="xs" onClick={() => savePick.mutate()} loading={savePick.isPending} disabled={!dirtyPick}>
                    Zapisz kompletację
                  </Button>
                </Group>
              )}
            </SectionCard>
            {p.issues.length > 0 && (
              <SectionCard title="Wydania" padding="sm">
                {p.issues.map((i) => (
                  <Text key={i.documentId} size="sm">
                    {formatDateTime(i.at)} · {i.number} · wydał {i.issuedByName ?? '—'} · odebrał {i.recipientName ?? '—'}: {i.lines.map((x) => `${x.name} ${qtyPl(x.quantity)} ${x.unit}`).join(', ')}
                  </Text>
                ))}
              </SectionCard>
            )}
            {open && (
              <Group justify="flex-end">
                {can(me, 'magazyn.kompletacja') && (
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={async () => {
                      if (await ask('Skompletowany towar wróci do rezerwacji tej realizacji; wydane zostaje.', { title: 'Anulować pakiet?', confirm: 'Anuluj pakiet', danger: true })) cancel.mutate('Anulowano w magazynie');
                    }}
                  >
                    Anuluj pakiet
                  </Button>
                )}
                {canPick && (
                  <Button variant="light" onClick={() => ready.mutate()} loading={ready.isPending} disabled={dirtyPick || !p.lines.some((l) => l.picked > 0)}>
                    Gotowy do odbioru
                  </Button>
                )}
                {can(me, 'magazyn.ruchy') && (
                  <Button onClick={() => setIssuing(true)} disabled={dirtyPick || !p.lines.some((l) => l.picked > 0)}>
                    Wydaj
                  </Button>
                )}
              </Group>
            )}
          </Stack>
        )}
      </QueryState>
      {issuing && p && <IssueModal p={p} onClose={() => setIssuing(false)} onDone={(r) => (done('Wydano z magazynu')(r), setIssuing(false))} />}
    </Drawer>
  );
}

function IssueModal({ p, onClose, onDone }: { p: PackageDto; onClose: () => void; onDone: (r: PackageDto) => void }) {
  const recipients = useRecipients();
  const team = recipients.data?.find((t) => t.id === p.teamId);
  const people = (team?.members ?? recipients.data?.flatMap((t) => t.members) ?? []).filter((m, i, a) => a.findIndex((x) => x.id === m.id) === i);
  const [recipient, setRecipient] = useState<string | null>(p.recipientUserId);
  // domyślnie lider ekipy (decyzja 7), gdy jest w dzisiejszym składzie
  useEffect(() => {
    if (!recipient && team?.leaderId && team.members.some((x) => x.id === team.leaderId)) setRecipient(team.leaderId);
  }, [team, recipient]);
  const [qty, setQty] = useState<Record<string, number>>(Object.fromEntries(p.lines.map((l) => [l.id, l.picked])));
  const [all, setAll] = useState(true);
  const requestKey = useMemo(newKey, []);
  const m = useMutation({
    mutationFn: () =>
      api<PackageDto>(`/packages/${p.id}/issue`, {
        method: 'POST',
        body: { recipientUserId: recipient, lines: p.lines.map((l) => ({ lineId: l.id, quantity: all ? l.picked : (qty[l.id] ?? 0) })), requestKey },
      }),
    onSuccess: onDone,
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Wydanie pakietu ${p.number}`} size="lg">
      <Stack>
        <Select label="Odbierający" required searchable data={people.map((x) => ({ value: x.id, label: x.name }))} value={recipient} onChange={setRecipient} />
        <Checkbox label="Wydaj wszystko, co skompletowane" checked={all} onChange={(e) => setAll(e.currentTarget.checked)} />
        {!all &&
          p.lines
            .filter((l) => l.picked > 0)
            .map((l) => (
              <Group key={l.id} justify="space-between">
                <Text size="sm">{l.name}</Text>
                <NumberInput size="xs" w={130} min={0} max={l.picked} decimalScale={3} decimalSeparator="," value={qty[l.id] ?? 0} onChange={(v) => setQty({ ...qty, [l.id]: Number(v) || 0 })} suffix={` ${l.unit}`} />
              </Group>
            ))}
        <Text size="xs" c="dimmed">
          Powstanie dokument {p.caseId ? 'WZ (wydanie na realizację)' : 'WE (zapas ekipy)'} — wydający: Ty, data i godzina: teraz. Niewydana reszta potrzeby zostaje otwarta.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!recipient}>
            Wydaj
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
